import { useLeaseCompData } from "@/shared/hooks/useLeaseCompData";
import { getMapVisibleProperties } from "@/shared/utils/propertyFiltering";
import { useAppSelector } from "@app/hooks";
import { useGetPOICategoriesQuery, useGetPOIsQuery } from "@/services/survey-poi";
import PDFFooter from "@components/pdf-partials/PDFFooter";
import PDFHeader from "@components/pdf-partials/PDFHeader";
import MarketMapImage from "@features/survey/components/marketMapPreview/MarketMapImage";
import MarketMapLegends from "@features/survey/components/marketMapPreview/MarketMapLegends";
import MarketMapPreview from "@features/survey/components/marketMapPreview/MarketMapPreview";
import MarketMapRenderedView from "@features/survey/components/marketMapPreview/MarketMapRenderedView";
import { useMarketMapLegendPages } from "@hooks/useMarketMapLegendPages";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import { selectVisibleLeaseComps } from "@survey/leaseCompsSelectors";
import classNames from "classnames";
import { useLocation, useSearchParams } from "react-router";
import { Fragment } from "react/jsx-runtime";

interface MarketMapWrapperProps {
  mode?: string;
}

interface MapContentProps {
  isMapRenderedView: boolean;
  isMarketMapPreviewUrl: boolean;
  pageIndex: number;
  isClientShortList: boolean;
}

const MapContent = ({
  isMapRenderedView,
  isMarketMapPreviewUrl,
  pageIndex,
  isClientShortList,
}: MapContentProps) => {
  if (isMapRenderedView) {
    return (
      <MarketMapRenderedView
        index={pageIndex}
        isClientShortList={isClientShortList}
      />
    );
  }
  if (isMarketMapPreviewUrl) {
    return <MarketMapImage />;
  }
  return (
    <MarketMapPreview index={pageIndex} isClientShortList={isClientShortList} />
  );
};

const MarketMapWrapper = ({ mode }: MarketMapWrapperProps) => {
  const survey = useAppSelector((state) => state.survey.currentSurvey);
  useLeaseCompData();

  // Fetch POI data on the preview route where Builder.tsx is not mounted
  useGetPOICategoriesQuery(survey?.id || "", {
    skip: !survey?.id,
    refetchOnMountOrArgChange: false,
  });
  useGetPOIsQuery(survey?.id || "", {
    skip: !survey?.id,
    refetchOnMountOrArgChange: false,
  });

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMarketMapPreviewUrl =
    location.pathname.includes("/preview/marketmap");
  const isMapRenderedView = location.pathname.includes(
    "/preview/renderedMarketMap",
  );

  // Get properties for legend count (computed before hooks that depend on them)
  const properties = survey?.properties || [];
  const displayFields = survey?.customLegends?.displayFields || {};
  const hasVisibleField = Object.values(displayFields).some(
    (isDisplayed) => isDisplayed === true,
  );
  const isClientShortList = searchParams.get("isClientShortList") === "true";
  const allVisibleLeaseComps = useAppSelector(selectVisibleLeaseComps);

  let visibleProperties = hasVisibleField
    ? getMapVisibleProperties(properties)
    : [];
  if (isClientShortList) {
    visibleProperties = visibleProperties.filter(
      (property) => property.isClientShortlist,
    );
  }

  const visibleLeaseComps = survey?.isIncludeLeaseComps
    ? allVisibleLeaseComps
    : [];

  const { totalItemsCount, totalPages, estimatedLegendHeight, getPageData } =
    useMarketMapLegendPages(survey, visibleProperties, visibleLeaseComps, mode);

  if (!survey) {
    return null;
  }

  const isPortrait =
    mode === "FlyerPreview" || survey?.pdfPaperSize === "Portrait";

  const renderMapPageWithLegends = (pageIndex: number) => {
    const {
      pageProperties,
      pageLeaseComps,
      numberedPropertiesBeforePage,
      startLeaseIndexFrom,
      hasPageContent,
    } = getPageData(pageIndex);

    const isFirstPage = pageIndex === 0;

    return (
      <SurveyPDFPage mode={mode}>
        <div
          className={classNames(
            "h-full w-full bg-white market-map-container grid",
            {
              "grid-rows-[auto_1fr_auto]": survey?.isIncludeHeader,
              "grid-rows-[1fr_auto]": !survey?.isIncludeHeader,
              "page-break-before": pageIndex > 0,
            },
          )}
        >
          {/* Header */}
          {survey?.isIncludeHeader && <PDFHeader title="Market Map" />}

          {/* Main Content Area */}
          <>
            {isPortrait ? (
              isFirstPage ? (
                // First page: Dynamic layout based on properties count
                <div className="flex flex-col">
                  <MapContent
                    isMapRenderedView={isMapRenderedView}
                    isMarketMapPreviewUrl={isMarketMapPreviewUrl}
                    pageIndex={pageIndex}
                    isClientShortList={isClientShortList}
                  />
                  {/* Legend Section - Portrait (At bottom) */}
                  {survey?.isIncludeLegend && hasPageContent && (
                    <div id="legend-section" className="mx-5">
                      <MarketMapLegends
                        mode={mode}
                        pageProperties={pageProperties}
                        startIndexFrom={numberedPropertiesBeforePage}
                        pageLeaseComps={pageLeaseComps}
                        startLeaseIndexFrom={startLeaseIndexFrom}
                      />
                    </div>
                  )}
                </div>
              ) : (
                // Subsequent pages: Fixed map height based on first page calculation
                <div className="flex flex-col h-full">
                  <div
                    className="shrink-0"
                    style={{
                      height: `calc(100% - ${estimatedLegendHeight}px)`,
                    }}
                  >
                    <MapContent
                      isMapRenderedView={isMapRenderedView}
                      isMarketMapPreviewUrl={isMarketMapPreviewUrl}
                      pageIndex={pageIndex}
                      isClientShortList={isClientShortList}
                    />
                  </div>
                  {/* Legend Section - Portrait (Right after map) */}
                  {survey?.isIncludeLegend && hasPageContent && (
                    <div className="legend-section mx-5">
                      <MarketMapLegends
                        mode={mode}
                        pageProperties={pageProperties}
                        startIndexFrom={numberedPropertiesBeforePage}
                        pageLeaseComps={pageLeaseComps}
                        startLeaseIndexFrom={startLeaseIndexFrom}
                      />
                    </div>
                  )}
                </div>
              )
            ) : (
              // Landscape Mode: Legend on left, Map on right
              <div
                className={classNames("h-full grid", {
                  "grid-cols-[340px_1fr]":
                    survey?.isIncludeLegend && hasPageContent,
                  "grid-cols-[1fr]":
                    !survey?.isIncludeLegend || !hasPageContent,
                })}
              >
                {survey?.isIncludeLegend && hasPageContent && (
                  <MarketMapLegends
                    mode={mode}
                    pageProperties={pageProperties}
                    startIndexFrom={numberedPropertiesBeforePage}
                    pageLeaseComps={pageLeaseComps}
                    startLeaseIndexFrom={startLeaseIndexFrom}
                  />
                )}

                <div className="min-h-0">
                  <MapContent
                    isMapRenderedView={isMapRenderedView}
                    isMarketMapPreviewUrl={isMarketMapPreviewUrl}
                    pageIndex={pageIndex}
                    isClientShortList={isClientShortList}
                  />
                </div>
              </div>
            )}
          </>

          <PDFFooter />
        </div>
      </SurveyPDFPage>
    );
  };

  // Render all pages with map and paginated legends, or just map page if no legends
  return survey?.isIncludeLegend && totalItemsCount > 0 ? (
    <>
      {Array.from({ length: totalPages }, (_, pageIndex) => (
        <Fragment key={pageIndex}>
          {renderMapPageWithLegends(pageIndex)}
        </Fragment>
      ))}
    </>
  ) : (
    renderMapPageWithLegends(0)
  );
};

export default MarketMapWrapper;
